import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabaseClient } from '../config/supabase';

export const SUPPORT_STORAGE_KEY = 'intelAgentActiveSupportGrant';

const SupportSessionContext = createContext({
    activeGrant: null,
    isSupportMode: false,
    isReadOnly: false,
    effectiveOrgId: null,
    timeRemaining: '',
    secondsRemaining: 0,
    annotations: [],
    isAnnotationMode: false,
    isAnnotating: false,
    sessionNotice: null,
    enterSupportSession: () => {},
    startSupportSession: () => {},
    exitSupportSession: () => {},
    toggleAnnotationMode: () => {},
    toggleAnnotating: () => {},
    addAnnotation: () => {},
    removeAnnotation: () => {},
    clearAnnotations: () => {},
    escalateSupportSession: () => {},
    clearSessionNotice: () => {}
});

export function formatTimeRemaining(seconds) {
    if (!seconds || seconds <= 0) return '00:00';
    const totalSecs = Math.floor(seconds);
    const hours = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    if (hours > 0) {
        return `${hours}h ${String(mins).padStart(2, '0')}m ${String(secs).padStart(2, '0')}s`;
    }
    return `${String(mins).padStart(2, '0')}m ${String(secs).padStart(2, '0')}s`;
}

export function formatCountdown(milliseconds) {
    if (!milliseconds || milliseconds <= 0) return '00:00';
    const totalSeconds = Math.floor(milliseconds / 1000);
    if (totalSeconds <= 0) return '00:00';
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hours > 0) {
        return `${hours}h ${String(mins).padStart(2, '0')}m`;
    }
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function SupportSessionProvider({ defaultOrgId, membershipOrgId, user, children }) {
    const baseOrgId = defaultOrgId || membershipOrgId || null;
    const [activeGrant, setActiveGrant] = useState(() => {
        try {
            const saved = sessionStorage.getItem(SUPPORT_STORAGE_KEY);
            if (!saved) return null;
            const parsed = JSON.parse(saved);
            const expires = new Date(parsed.access_expires_at).getTime();
            if (expires > Date.now()) {
                return parsed;
            }
            sessionStorage.removeItem(SUPPORT_STORAGE_KEY);
            return null;
        } catch {
            return null;
        }
    });

    const [annotations, setAnnotations] = useState([]);
    const [isAnnotationMode, setIsAnnotationMode] = useState(false);
    const [sessionNotice, setSessionNotice] = useState(null);
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const channelRef = useRef(null);

    const isSupportMode = Boolean(activeGrant);
    const isReadOnly = isSupportMode;
    const effectiveOrgId = isSupportMode ? activeGrant.org_id : baseOrgId;

    const clearStoredSupportSession = useCallback(() => {
        try {
            sessionStorage.removeItem(SUPPORT_STORAGE_KEY);
        } catch {
            // ignore
        }
        if (channelRef.current && typeof supabaseClient.removeChannel === 'function') {
            try {
                supabaseClient.removeChannel(channelRef.current);
            } catch (e) {
                console.warn('Error removing realtime channel:', e);
            }
            channelRef.current = null;
        }
        setActiveGrant(null);
        setAnnotations([]);
        setIsAnnotationMode(false);
        setSecondsRemaining(0);
    }, []);

    const updateStoredActiveGrant = useCallback((nextGrant) => {
        setActiveGrant(nextGrant);
        try {
            sessionStorage.setItem(SUPPORT_STORAGE_KEY, JSON.stringify(nextGrant));
        } catch {
            // ignore
        }
    }, []);

    const exitSupportSession = useCallback(async (reason, options = {}) => {
        const shouldRevoke = Boolean(options?.revoke && activeGrant?.id);
        let revokeError = null;

        if (activeGrant?.id) {
            supabaseClient.rpc('log_support_session_event', {
                p_grant_id: activeGrant.id,
                p_action: 'session_exited',
                p_detail: null
            }).catch((err) => {
                console.warn('audit log session_exited failed (non-fatal):', err);
            });
        }

        if (shouldRevoke) {
            try {
                const { error } = await supabaseClient.rpc('revoke_support_access_grant', {
                    p_grant_id: activeGrant.id
                });
                if (error) {
                    throw error;
                }
            } catch (err) {
                console.error('Error revoking support grant on exit:', err);
                revokeError = err;
            }
        }

        clearStoredSupportSession();
        if (reason) {
            setSessionNotice(revokeError
                ? `Support session closed locally, but the server revoke failed: ${revokeError.message || 'Unknown error'}`
                : reason);
        }
    }, [activeGrant?.id, clearStoredSupportSession]);

    const enterSupportSession = useCallback((grant, orgName = '') => {
        if (!grant || !grant.id || !grant.org_id) {
            console.error('Invalid grant provided to enterSupportSession:', grant);
            return;
        }
        const expires = new Date(grant.access_expires_at).getTime();
        if (Number.isNaN(expires) || expires <= Date.now()) {
            setSessionNotice('Cannot enter session: Support access grant has already expired.');
            return;
        }

        const sessionPayload = {
            id: grant.id,
            org_id: grant.org_id,
            org_name: orgName || grant.org_name || grant.org_id,
            reason: grant.reason,
            support_user_id: grant.support_user_id,
            access_expires_at: grant.access_expires_at,
            escalated_at: grant.escalated_at || null,
            escalated_issue_url: grant.escalated_issue_url || null,
            status: grant.status || 'redeemed',
            entered_at: new Date().toISOString()
        };

        try {
            sessionStorage.setItem(SUPPORT_STORAGE_KEY, JSON.stringify(sessionPayload));
        } catch {
            // ignore
        }
        setSessionNotice(null);
        setAnnotations([]);
        setIsAnnotationMode(false);
        updateStoredActiveGrant(sessionPayload);

        supabaseClient.rpc('log_support_session_event', {
            p_grant_id: sessionPayload.id,
            p_action: 'session_entered',
            p_detail: null
        }).catch((err) => {
            console.warn('audit log session_entered failed (non-fatal):', err);
        });
    }, [updateStoredActiveGrant]);

    const escalateSupportSession = useCallback(async (findings) => {
        if (!activeGrant?.id) {
            return { error: new Error('No active support session is available to escalate.') };
        }
        if (!supabaseClient.functions?.invoke) {
            return { error: new Error('Support escalation function is not available in this environment.') };
        }

        const { data, error } = await supabaseClient.functions.invoke('escalate-support-session', {
            body: {
                grantId: activeGrant.id,
                orgName: activeGrant.org_name || activeGrant.org_id,
                reason: activeGrant.reason,
                findings,
                annotations
            }
        });

        if (error) {
            setSessionNotice(`Support escalation failed: ${error.message || 'Unknown error'}`);
            return { error };
        }

        const nextGrant = {
            ...activeGrant,
            escalated_at: data?.escalatedAt || new Date().toISOString(),
            escalated_issue_url: data?.issueUrl || data?.escalatedIssueUrl || activeGrant.escalated_issue_url || null
        };
        updateStoredActiveGrant(nextGrant);
        setSessionNotice('Support session escalated to Engineering.');
        return { data: { ...data, grant: nextGrant } };
    }, [activeGrant, annotations, updateStoredActiveGrant]);

    // Countdown timer effect
    useEffect(() => {
        if (!activeGrant) {
            setSecondsRemaining(0);
            return;
        }

        const updateTimer = () => {
            const expires = new Date(activeGrant.access_expires_at).getTime();
            const now = Date.now();
            const diffSeconds = Math.max(0, Math.floor((expires - now) / 1000));
            setSecondsRemaining(diffSeconds);

            if (diffSeconds <= 0) {
                exitSupportSession('Support access grant expired.', { revoke: false });
            }
        };

        updateTimer();
        const intervalId = setInterval(updateTimer, 1000);
        return () => clearInterval(intervalId);
    }, [activeGrant, exitSupportSession]);

    // Realtime channel for live grant revocation & annotations
    useEffect(() => {
        if (!activeGrant?.id || typeof supabaseClient.channel !== 'function') {
            return;
        }

        try {
            const channelName = `support-session-${activeGrant.id}`;
            const channel = supabaseClient.channel(channelName, {
                config: { broadcast: { self: false } }
            });

            if (!channel || typeof channel.on !== 'function') {
                return;
            }

            channel
                .on('broadcast', { event: 'annotation' }, ({ payload }) => {
                    if (payload && payload.id) {
                        setAnnotations((prev) => {
                            const exists = prev.some((a) => a.id === payload.id);
                            if (exists) return prev;
                            return [...prev.slice(-29), payload];
                        });
                    }
                })
                .on('broadcast', { event: 'clear_annotations' }, () => {
                    setAnnotations([]);
                })
                .on('broadcast', { event: 'remove_annotation' }, ({ payload }) => {
                    if (payload?.id) {
                        setAnnotations((prev) => prev.filter((a) => a.id !== payload.id));
                    }
                })
                .on('postgres_changes', {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'support_access_grants',
                    filter: `id=eq.${activeGrant.id}`
                }, (payload) => {
                    const nextStatus = payload?.new?.status;
                    if (nextStatus === 'revoked' || nextStatus === 'expired') {
                        exitSupportSession(`Support access grant was ${nextStatus} by tenant administrator.`, { revoke: false });
                    }
                });

            channel.subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    // Ready
                }
            });

            channelRef.current = channel;

            return () => {
                if (channel && typeof supabaseClient.removeChannel === 'function') {
                    try {
                        supabaseClient.removeChannel(channel);
                    } catch (e) {
                        console.warn('Error removing channel:', e);
                    }
                }
                channelRef.current = null;
            };
        } catch (e) {
            console.warn('Supabase Realtime not supported or failed to connect:', e);
        }
    }, [activeGrant?.id, exitSupportSession]);

    const toggleAnnotationMode = useCallback((val) => {
        setIsAnnotationMode((prev) => (typeof val === 'boolean' ? val : !prev));
    }, []);

    const addAnnotation = useCallback(({ x, y, xPercent, yPercent, text, color = '#f97316' }) => {
        const rawX = xPercent !== undefined ? xPercent : (x !== undefined ? x : 50);
        const rawY = yPercent !== undefined ? yPercent : (y !== undefined ? y : 50);
        const newAnnotation = {
            id: `ann-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            xPercent: Math.max(2, Math.min(98, rawX)),
            yPercent: Math.max(2, Math.min(98, rawY)),
            text: text || 'Support Marker',
            author: user?.email || 'Support Specialist',
            color,
            createdAt: new Date().toISOString()
        };

        setAnnotations((prev) => [...prev.slice(-29), newAnnotation]);

        if (channelRef.current && typeof channelRef.current.send === 'function') {
            try {
                channelRef.current.send({
                    type: 'broadcast',
                    event: 'annotation',
                    payload: newAnnotation
                });
            } catch (e) {
                console.warn('Failed to broadcast annotation:', e);
            }
        }
    }, [user?.email]);

    const removeAnnotation = useCallback((id) => {
        setAnnotations((prev) => prev.filter((a) => a.id !== id));
        if (channelRef.current && typeof channelRef.current.send === 'function') {
            try {
                channelRef.current.send({
                    type: 'broadcast',
                    event: 'remove_annotation',
                    payload: { id }
                });
            } catch (e) {
                console.warn('Failed to broadcast remove_annotation:', e);
            }
        }
    }, []);

    const clearAnnotations = useCallback(() => {
        setAnnotations([]);
        if (channelRef.current && typeof channelRef.current.send === 'function') {
            try {
                channelRef.current.send({
                    type: 'broadcast',
                    event: 'clear_annotations',
                    payload: {}
                });
            } catch (e) {
                console.warn('Failed to broadcast clear_annotations:', e);
            }
        }
    }, []);

    const clearSessionNotice = useCallback(() => {
        setSessionNotice(null);
    }, []);

    const timeRemaining = formatTimeRemaining(secondsRemaining);

    const value = {
        activeGrant,
        isSupportMode,
        isReadOnly,
        effectiveOrgId,
        timeRemaining,
        secondsRemaining,
        annotations,
        isAnnotationMode,
        isAnnotating: isAnnotationMode,
        sessionNotice,
        enterSupportSession,
        startSupportSession: enterSupportSession,
        exitSupportSession,
        toggleAnnotationMode,
        toggleAnnotating: toggleAnnotationMode,
        addAnnotation,
        removeAnnotation,
        clearAnnotations,
        escalateSupportSession,
        clearSessionNotice
    };

    return (
        <SupportSessionContext.Provider value={value}>
            {children}
        </SupportSessionContext.Provider>
    );
}

export function useSupportSession() {
    return useContext(SupportSessionContext);
}

export function SupportWriteGuard({
    children,
    fallback = null,
    disabled = false,
    mode = 'hide',
    tooltip = 'Action disabled in read-only support session'
}) {
    const { isSupportMode } = useSupportSession();
    if (!isSupportMode) {
        return children;
    }
    const shouldDisable = disabled || mode === 'disable' || mode === 'disabled';
    if (shouldDisable && React.isValidElement(children)) {
        return React.cloneElement(children, {
            disabled: true,
            title: tooltip,
            style: { ...(children.props.style || {}), opacity: 0.5, cursor: 'not-allowed' }
        });
    }
    return fallback;
}

export default SupportSessionContext;
